import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import {
  AROUND,
  EMPTY_RESULTS_SHOW,
  FIELD_REQUIRED,
  LOADING,
  TYPE_LEAST_CHARACTERS,
} from "../../i18n/keysTranslations";
import { Controller, useController } from "react-hook-form";
import i18next from "i18next";
import Typography from "@mui/material/Typography";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { useDebounced } from "../../hooks/useDebounce";
import { startGetAdsCampaignSegmentation } from "../../actions/adsCampaigns";
import {
  ADS_CAMPAIGN_SEARCH_SEGMENTATION_INTERESTS_TYPE,
  ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE,
  DELAY_DEBOUNCE,
  FROM_MAP_RADIUS_COMPONENT,
  META,
} from "../../utils/constants";
import { getReactHookFormNestedError } from "../../utils/errors";
import Tooltip from "@mui/material/Tooltip";
import { numberDecimal } from "../../utils/numbers";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import FieldOpenMapRadius from "../Maps/FieldOpenMapRadius";

const SearchSegmentationsField = ({
  control,
  errors = {},
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  groupBy,
  orderBy = "asc",
  disabled = false,
  placeholder,
  variant = "filled",
  fullWidth,
  helperText,
  type = "interests",
  mode = "fetching",
  platform,
  integrationID,
  adAccountID,
  objective,
  language,
  sxTextField = {},
  sx = {},
  actionsChange = () => {},
  fixedOptions = [],
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const error = getReactHookFormNestedError(errors, name);
  const [inputValue, setInputValue] = useState("");

  const {
    // eslint-disable-next-line no-unused-vars
    field: { value: currentValue, onChange: onChangeField },
  } = useController({ name, control });

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const hasFixedOptions = fixedOptions?.length > 0;

  // Get value by path
  const getValueByPath = (obj, path) => {
    return path.reduce((acc, key) => acc[key], obj);
  };

  const getOptionDisplayLabel = (option) => {
    if (option?.labelKey) {
      return t(option.labelKey);
    }
    return option?.name ?? "";
  };

  const optionsFromSource = hasFixedOptions ? fixedOptions : options;
  const optionsFiltered = hasFixedOptions
    ? inputValue
      ? optionsFromSource.filter((opt) =>
          getOptionDisplayLabel(opt)
            .toLowerCase()
            .includes(inputValue.toLowerCase())
        )
      : optionsFromSource
    : inputValue
      ? optionsFromSource
      : [];

  // Sort by groupBy
  const sortByGroupBy = () => {
    const newOptions = [...optionsFiltered];
    return newOptions.sort((a, b) => {
      for (let path of groupBy) {
        const aValue = getValueByPath(a, Array.isArray(path) ? path : [path]);
        const bValue = getValueByPath(b, Array.isArray(path) ? path : [path]);

        if (aValue > bValue) {
          return orderBy === "asc" ? 1 : -1;
        }
        if (aValue < bValue) {
          return orderBy === "asc" ? -1 : 1;
        }
      }
      return 0;
    });
  };

  const getTextExtended = (option) => {
    if (type === ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE) {
      return (
        `${option?.region ? `${option?.region},` : ""} ${
          option?.country_name
        }` || option?.region
      );
    }

    return option?.path?.[0];
  };

  const getAdsCampaignSegmentation = useDebounced(
    async (textQuery) => {
      if (!integrationID || !adAccountID) return;
      if (mode === "load" && optionsFiltered?.length > 0) return;
      setLoading(true);
      const response = await dispatch(
        startGetAdsCampaignSegmentation({
          keyWord: textQuery,
          type,
          platform,
          objective,
          language,
          integrationID,
          adAccountID,
        })
      );
      if (response) {
        setOptions((prevState) => {
          //filter out the options that are already in the state
          const newOptions = response.filter(
            (option) =>
              !prevState.some((prevOption) => prevOption.name === option.name)
          );

          return [...prevState, ...newOptions];
        });
      }
      setLoading(false);
    },
    [DELAY_DEBOUNCE]
  );

  useEffect(() => {
    if (disabled) return;
    // eslint-disable-next-line
  }, [mode, disabled]);

  useEffect(() => {
    if (!groupBy) return;

    const sortedOptions = sortByGroupBy();
    if (sortedOptions.length !== optionsFiltered.length) {
      setOptions(sortedOptions);
    }
    // eslint-disable-next-line
  }, [options, groupBy]);

  return (
    <>
      <Controller
        control={control}
        name={name}
        defaultValue={[]}
        rules={rules}
        render={({ field }) => {
          const { key: someKey, ...rest } = field;
          return (
            <>
              <Autocomplete
                {...rest}
                id={name}
                key={someKey}
                sx={{
                  ...sx,
                  "& .MuiAutocomplete-endAdornment": {
                    top: "calc(50% - 14px)",
                  },
                }}
                fullWidth={fullWidth}
                disabled={disabled}
                open={open}
                hasLabel={Boolean(label)}
                multiple={true}
                groupBy={(option) => {
                  if (!groupBy) return;
                  const hasMoreOneGroup = groupBy.length > 1;

                  if (!hasMoreOneGroup) return option[groupBy[0]];

                  return option[groupBy[0]][groupBy[1]];
                }}
                onOpen={() => setOpen(true)}
                onClose={() => {
                  setOpen(false);
                }}
                onChange={(_, value) => {
                  field.onChange(value);
                  actionsChange(value);
                }}
                onInputChange={(_, value) => {
                  setInputValue(value);
                  if (!hasFixedOptions && value.length > 2) {
                    getAdsCampaignSegmentation(value);
                  }
                }}
                filterOptions={hasFixedOptions ? (x) => x : undefined}
                noOptionsText={
                  <Typography sx={{ color: "rgba(0, 0, 0, 0.7) !important" }}>
                    {t(
                      hasFixedOptions
                        ? EMPTY_RESULTS_SHOW
                        : optionsFiltered.length === 0
                          ? TYPE_LEAST_CHARACTERS
                          : EMPTY_RESULTS_SHOW,
                      { value: 3 }
                    )}
                  </Typography>
                }
                loadingText={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CircularProgress color="inherit" size={20} />
                    {t(LOADING)}
                  </Box>
                }
                isOptionEqualToValue={(option, value) => {
                  const optionId = option?.id;
                  const valueId = value?.id;
                  return optionId && valueId
                    ? optionId === valueId
                    : option?.name === value?.name;
                }}
                getOptionLabel={(option) => getOptionDisplayLabel(option)}
                options={optionsFiltered}
                loading={loading}
                renderOption={(props, option) => {
                  const { key: someKey, ...rest } = props;
                  return (
                    <Box
                      {...rest}
                      key={someKey}
                      component="li"
                      sx={{ display: "flex", gap: 1 }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {getOptionDisplayLabel(option)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "fields.placeholder",
                          fontStyle: "italic",
                        }}
                      >
                        {getTextExtended(option)}
                      </Typography>
                    </Box>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    if (
                      platform === META &&
                      type === ADS_CAMPAIGN_SEARCH_SEGMENTATION_INTERESTS_TYPE
                    ) {
                      return (
                        <Tooltip
                          sx={{ backgroundColor: "#1E1E1E" }}
                          key={key}
                          title={
                            option?.disambiguation_category ||
                            option?.topic ||
                            option?.description
                          }
                        >
                          <Chip
                            label={getOptionDisplayLabel(option)}
                            {...tagProps}
                            sx={{
                              backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                              border: `1px solid ${INPUT_COLOR_BORDER}`,
                            }}
                          />
                        </Tooltip>
                      );
                    }
                    if (
                      type ===
                        ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE &&
                      option.type === "custom"
                    ) {
                      return (
                        <Chip
                          key={key}
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="body2">
                                {getOptionDisplayLabel(option)}
                              </Typography>
                              {!option.subType && (
                                <Typography variant="body2">{`(${numberDecimal(
                                  option.radius / 1000
                                )} km ${t(AROUND)})`}</Typography>
                              )}
                            </Box>
                          }
                          {...tagProps}
                          sx={{
                            backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                            border: `1px solid ${INPUT_COLOR_BORDER}`,
                          }}
                        />
                      );
                    }
                    return (
                      <Chip
                        key={key}
                        label={getOptionDisplayLabel(option)}
                        {...tagProps}
                        sx={{
                          backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                          border: `1px solid ${INPUT_COLOR_BORDER}`,
                        }}
                      />
                    );
                  })
                }
                renderInput={(params) => {
                  const { key: someKey, ...rest } = params;
                  return (
                    <TextField
                      {...rest}
                      onClick={(e) => {
                        if (
                          type !==
                            ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE ||
                          disabled
                        )
                          return;
                        e?.stopPropagation();
                        e?.preventDefault();
                        const locationsInput = document.getElementById(
                          `${name}-map-radius`
                        );
                        if (locationsInput) {
                          locationsInput.click();
                        }
                      }}
                      key={someKey}
                      label={label}
                      hiddenLabel={!label}
                      variant={variant}
                      placeholder={field?.value?.length > 0 ? "" : placeholder}
                      error={!!error}
                      sx={{
                        backgroundColor: "fields.background",
                        border: `1px solid ${INPUT_COLOR_BORDER}`,
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                        ...sxTextField,
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  );
                }}
              />
              {type === ADS_CAMPAIGN_SEARCH_SEGMENTATION_LOCATIONS_TYPE && (
                <FieldOpenMapRadius
                  idField={`${name}-map-radius`}
                  name={name}
                  from={FROM_MAP_RADIUS_COMPONENT.AD_GROUP}
                  onCallbackSave={(response) => {
                    const locations = response.locations;
                    onChangeField(locations);
                  }}
                  showButtons={false}
                  platform={platform}
                  integrationID={integrationID}
                  adAccountID={adAccountID}
                  sx={{
                    display: "none",
                  }}
                />
              )}
            </>
          );
        }}
      />
      {error?.message && (
        <Typography
          variant="caption"
          color={"error"}
          sx={{
            display: "block",
          }}
        >
          {error?.message}
        </Typography>
      )}
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
          }}
          color={"fields.placeholder"}
          dangerouslySetInnerHTML={{
            __html: helperText,
          }}
        />
      )}
    </>
  );
};

export default SearchSegmentationsField;
